/**
 * AddExternalUserCtrl controller
 * Add/Edit External Users
 */

import { has, property } from 'app/helpers/object'
import { flatten, toMap } from 'app/helpers/array'
import { pickId, getNetwork, unique, remove, add } from 'app/helpers/entity'
import { SUBSCRIPTION_TYPES } from 'app/core/constants'

/**
 * AddExternalUserCtrl controller
 *
 * @class AddExternalUserCtrl
 * @memberof mwApp/settings
 * @see mwApp/settings
 *
 * @ngdoc controller
 * @name mwApp/settings.controller:AddExternalUserCtrl
 */
/* @ngInject */
export default function AddExternalUserCtrl ($scope, $http, ExternalUser, Subscription, ExternalUserExtraNetwork, user) {
  var vm = this

  /**
   * Loader indicator
   *
   * @type {boolean}
   */
  $scope.loading = true

  /**
   * External User
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @type {models.ExternalUser}
   */
  vm.user = user

  /**
   * isNew flag
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @type {Boolean}
   */
  vm.isNew = user._isNew()

  /**
   * Subscription types
   *
   * @memberof mwApp/packages.controller:AddExternalUserCtrl
   * @type {Object}
   */
  vm.subscription_types = SUBSCRIPTION_TYPES

  /**
   * Assigned Subscriptions (main & add ons); structure - {networkId: {name: 'NetworkName', subscriptions: [], addOns: []}}
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @type {Array}
   */
  vm.assignedSubscriptions =
    user.subscriptions.concat(user.addOns)
      .map(getNetwork)
      .filter(unique)
      .map(n => new ExternalUserExtraNetwork(n))
      .reduce(toMap(), Object.create(null))

  // fill subscribtions and addons in vm.assignedSubscriptions
  user.subscriptions.forEach(s => vm.assignedSubscriptions[s.network.id].subscriptions.push(s))
  user.addOns.forEach(a => vm.assignedSubscriptions[a.network.id].addOns.push(a))

  /**
   * Subscriptions
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @type {Array}
   */
  vm.subscriptions = []

  /**
   * AddOns
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @type {Array}
   */
  vm.addOns = []

  // load extra data for external user
  ExternalUser.loadExtra(ExternalUser.extraFor(user))
    .to(vm)
    .then(
      data => {
        // get all subscriptions from extra data
        vm.subscriptions = flatten(vm.networks.map(property('subscriptions')))

        // get addons from extra data
        const ids = Object.keys(vm.assignedSubscriptions)
        const inIds = n => ids.indexOf(String(n.id)) !== -1
        vm.addOns = flatten(vm.networks.filter(inIds).map(property('addOns')))
        $scope.loading = false
      }
    )

  /**
   * Create or edit external user
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   */
  vm.submit = function () {
    vm.setDisable()

    const ids = Object.keys(vm.assignedSubscriptions)
    const assignedSubscriptions = flatten(ids.map(id => vm.assignedSubscriptions[id].subscriptions))
    const assignedAddOns = flatten(ids.map(id => vm.assignedSubscriptions[id].addOns))

    user._save(
      user => {
        // get subscriptions and addons from assigned subscriptions
        user.subscriptions = assignedSubscriptions.map(pickId)
        user.addOns = assignedAddOns.map(pickId)
      }
    ).then(vm.location(user).reload)
      .catch(() => {})
      .finally(vm.unsetDisable)
  }

  /**
   * Delete external user
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   */
  vm.deleteUser = function () {
    vm.setDisable()
    user._delete()
      .then(vm.location(user).list)
      .catch(() => {})
      .finally(vm.unsetDisable)
  }

  /**
   * Assign subscription to external user
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @param {models.Subscription} subscription
   */
  vm.assignSubscription = function (subscription) {
    if (subscription) {
      const networkId = subscription.network.id

      if (!has(networkId)(vm.assignedSubscriptions)) {
        vm.assignedSubscriptions[networkId] = new ExternalUserExtraNetwork(subscription.network) // Note: generate new structure
        vm.addOns = vm.addOns.concat(vm.networks.find(n => n.id === networkId).addOns) // Note: add actual add ons by network
      }

      add(vm.assignedSubscriptions[networkId].subscriptions)(subscription)() // add subscription to assigned
      remove(vm.subscriptions)(subscription)() // remove subscription from available

      // empty selected subscription
      vm.selectedSubscription = null
    }
  }

  /**
   * Unassign subscription to external user
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @param {models.Subscription} subscription
   * @param form
   */
  vm.unassignSubscription = function (subscription, form) {
    if (subscription) {
      form.$setDirty()
      const networkId = subscription.network.id

      add(vm.subscriptions)(subscription)() // add subscription to available
      remove(vm.assignedSubscriptions[networkId].subscriptions)(subscription)() // remove subscription from assigned

      if (vm.assignedSubscriptions[networkId].subscriptions.length === 0) { // if assigned subscriptions is empty - remove network container
        delete vm.assignedSubscriptions[networkId]
        vm.addOns = vm.addOns.filter(a => a.network.id !== networkId)
      }

      // empty selected subscription
      vm.selectedSubscription = null
    }
  }

  /**
   * Assign addOns to external user
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @param {models.Subscription} addOn
   */
  vm.assignAddOns = function (addOn) {
    if (addOn) {
      const networkId = addOn.network.id

      add(vm.assignedSubscriptions[networkId].addOns)(addOn)() // add addOn to assigned
      remove(vm.addOns)(addOn)() // remove addOn from available

      // empty selected addon
      vm.selectedAddOn = null
    }
  }

  /**
   * Unassign addOns to external user
   *
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @param {models.Subscription} addOn
   * @param form
   */
  vm.unassignAddOns = function (addOn, form) {
    if (addOn) {
      form.$setDirty()

      const networkId = addOn.network.id

      add(vm.addOns)(addOn)() // add addOn to available
      remove(vm.assignedSubscriptions[networkId].addOns)(addOn)() // remove addOn from assigned

      // empty selected addon
      vm.selectedAddOn = null
    }
  }

  /**
   * Return `true` if user has assigned subscriptions, `false` otherwise
   *
   * @function
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @return {Boolean}
   */
  vm.hasSubscriptions = () => {
    for (var id in vm.assignedSubscriptions) {
      return true
    }
    return false
  }

  /**
   * Return network name for item (used in ui-select for grouping)
   *
   * @function
   * @memberof mwApp/settings.controller:AddExternalUserCtrl
   * @param {Object} item
   * @return {String}
   */
  vm.groupByNetwork = item => item.network.name
}
